// Spur-Audio für die Vocal-Analyse: Mono-Signal einer Spur in Timeline-Zeit (wie es klingt, inklusive Warp),
// ohne Clip Gain und Fades. Grundlage für Vocal-Timing (Silben, Ausrichten auf eine Leitspur).
import type { Project } from '@srs/shared';
import { engine } from './engine.ts';
import { clipMap } from '../edit/warp.ts';
import { alignMap, envelopeDb, olaRead, gridTargets, guideTargets, vocalOnsets, type GridTimingOpts, type TimingEvent, type VOnset } from '../edit/vocal.ts';

export const SCAN_SR = 24000;

/** Mono-Signal der Spur von a bis b (s), SCAN_SR, Tonhöhe wie gespielt (auch bei Warp); null, wenn kein Audio geladen ist */
export function trackSignal(p: Project, trackId: string, a: number, b: number, sr = SCAN_SR): Float32Array | null {
  const n = Math.max(0, Math.round((b - a) * sr));
  const clips = p.clips.filter((c) => c.trackId === trackId && c.kind === 'audio' && !c.muted && c.sourceId && engine.buffers.get(c.sourceId) && c.start < b && c.start + c.duration > a);
  if (!clips.length) return null;
  const x = new Float32Array(n);
  // je Quelle einmal lesen; jeder Clip nur in seinem Bereich (oberster Clip gewinnt)
  for (const c of clips) {
    const buf = engine.buffers.get(c.sourceId!)!, chs = Array.from({ length: buf.numberOfChannels }, (_, k) => buf.getChannelData(k));
    const mono = (i: number) => { let v = 0; for (const ch of chs) v += ch[i]; return v / chs.length; };
    const m = clipMap(p, c), top = (t: number) => { let w = null as (typeof clips)[number] | null; for (const k of clips) if (t >= k.start && t < k.start + k.duration) w = k; return w === c; };
    const y = olaRead(mono, buf.length, buf.sampleRate, (t) => (top(t) ? m.toSrc(t) : null), a, n, sr);
    for (let i = 0; i < n; i++) x[i] += y[i];
  }
  return x;
}

export interface TimingPlan { trackId: string; onsets: VOnset[]; events: TimingEvent[] }
export type TimingTarget = { kind: 'grid'; opts: GridTimingOpts } | { kind: 'guide'; guideId: string; maxShiftMs: number; strength: number };

/** Silben je Spur erkennen und Ziele bestimmen (Raster oder Leitspur) */
export function planTiming(p: Project, r: { start: number; end: number; trackIds: string[] }, target: TimingTarget, sensitivity: number): TimingPlan[] {
  const out: TimingPlan[] = [];
  const guide = target.kind === 'guide' ? trackSignal(p, target.guideId, r.start, r.end) : null;
  const gOn = guide ? vocalOnsets(guide, SCAN_SR, r.start, sensitivity) : [];
  for (const id of r.trackIds) {
    if (target.kind === 'guide' && id === target.guideId) continue;
    const x = trackSignal(p, id, r.start, r.end);
    if (!x) continue;
    const onsets = vocalOnsets(x, SCAN_SR, r.start, sensitivity);
    let events: TimingEvent[];
    if (target.kind === 'grid') events = gridTargets(p, onsets, target.opts);
    else {
      if (!guide) continue;
      const map = alignMap(guide, x, SCAN_SR, r.start, target.maxShiftMs / 1000);
      const E = envelopeDb(x, SCAN_SR); let mx = -Infinity; for (const v of E) if (v > mx) mx = v;
      const voiced = (t: number) => { const k = Math.round((t - r.start - 0.01) / 0.005); return k >= 0 && k < E.length && E[k] > mx - 30; };
      events = guideTargets(onsets, map, voiced, r.start, r.end, target.strength, gOn);
    }
    out.push({ trackId: id, onsets, events });
  }
  return out;
}
